(function (root, factory) {
  var shippingOrigin = factory(root);

  if (typeof module === "object" && module.exports) {
    module.exports = shippingOrigin;
    return;
  }

  root.LevyShippingOrigin = shippingOrigin;

  function start() {
    shippingOrigin.init(root.document);
  }

  if (root.document.readyState === "loading") {
    root.document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  root.document.addEventListener("shopify:section:load", start);
  root.document.addEventListener("venue:variant:updated", function (event) {
    shippingOrigin.handleVariantUpdate(event, root.document);
  });
})(typeof window !== "undefined" ? window : globalThis, function (root) {
  "use strict";

  var CACHE_KEY = "levy:shipping-origin:v1";
  var GEO_ENDPOINT = "https://get.geojs.io/v1/ip/geo.json";
  var ORIGINS = {
    CALIFORNIA: "california",
    NEW_YORK: "new-york",
    UNITED_STATES: "united-states"
  };
  var FULFILLMENT_HUBS = {
    california: { latitude: 34.0522, longitude: -118.2437 },
    "new-york": { latitude: 40.7128, longitude: -74.006 }
  };
  var lookupPromise;

  function toRadians(value) {
    return value * Math.PI / 180;
  }

  function distanceInKilometers(latitude, longitude, destination) {
    var earthRadius = 6371;
    var latitudeDelta = toRadians(destination.latitude - latitude);
    var longitudeDelta = toRadians(destination.longitude - longitude);
    var startLatitude = toRadians(latitude);
    var endLatitude = toRadians(destination.latitude);
    var haversine = Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
      Math.cos(startLatitude) * Math.cos(endLatitude) *
      Math.sin(longitudeDelta / 2) * Math.sin(longitudeDelta / 2);

    return earthRadius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  function nearestOrigin(latitude, longitude) {
    var parsedLatitude = Number(latitude);
    var parsedLongitude = Number(longitude);

    if (!Number.isFinite(parsedLatitude) || !Number.isFinite(parsedLongitude)) {
      return ORIGINS.UNITED_STATES;
    }

    var californiaDistance = distanceInKilometers(
      parsedLatitude,
      parsedLongitude,
      FULFILLMENT_HUBS.california
    );
    var newYorkDistance = distanceInKilometers(
      parsedLatitude,
      parsedLongitude,
      FULFILLMENT_HUBS["new-york"]
    );

    return californiaDistance <= newYorkDistance
      ? ORIGINS.CALIFORNIA
      : ORIGINS.NEW_YORK;
  }

  function originFromGeo(location) {
    if (!location || String(location.country_code || "").toUpperCase() !== "US") {
      return ORIGINS.UNITED_STATES;
    }

    return nearestOrigin(location.latitude, location.longitude);
  }

  function variantIsInStock(variant) {
    if (!variant || !variant.available) {
      return false;
    }

    if (!variant.inventory_management) {
      return true;
    }

    return Number(variant.inventory_quantity) > 0;
  }

  // Same rule theme.js uses for the "Pre-Order Now" button.
  function variantIsPreorder(variant) {
    if (!variant) {
      return false;
    }

    return variant.inventory_policy === "continue" &&
      Number(variant.inventory_quantity) <= 0;
  }

  function calloutHidesPreorder(callout) {
    return Boolean(callout && callout.dataset && callout.dataset.hideForPreorder === "true");
  }

  // A callout rendered with hide_for_preorder (the Levy Max template) never shows the
  // next-business-day promise while the variant is in preorder state.
  function variantShowsPromise(variant, callout) {
    if (!variantIsInStock(variant)) {
      return false;
    }

    if (calloutHidesPreorder(callout) && variantIsPreorder(variant)) {
      return false;
    }

    return true;
  }

  function readCachedOrigin() {
    try {
      var cachedOrigin = root.sessionStorage.getItem(CACHE_KEY);
      return Object.keys(ORIGINS).some(function (key) {
        return ORIGINS[key] === cachedOrigin;
      }) ? cachedOrigin : null;
    } catch (error) {
      return null;
    }
  }

  function cacheOrigin(origin) {
    try {
      root.sessionStorage.setItem(CACHE_KEY, origin);
    } catch (error) {
      // Storage can be disabled; the callout still works for the current page.
    }
  }

  function copyForOrigin(callout, origin) {
    if (origin === ORIGINS.CALIFORNIA) {
      return callout.dataset.copyCalifornia;
    }

    if (origin === ORIGINS.NEW_YORK) {
      return callout.dataset.copyNewYork;
    }

    return callout.dataset.copyUnitedStates;
  }

  function updateCallouts(callouts, origin) {
    callouts.forEach(function (callout) {
      var text = callout.querySelector("[data-shipping-origin-text]");
      callout.dataset.origin = origin;
      callout.removeAttribute("aria-busy");

      if (text) {
        text.textContent = copyForOrigin(callout, origin);
      }
    });
  }

  function updateInventoryState(callouts, inStock) {
    callouts.forEach(function (callout) {
      callout.dataset.inStock = String(inStock);
      callout.hidden = !inStock;
    });
  }

  function lookupOrigin(endpoint) {
    if (!lookupPromise) {
      var controller = typeof root.AbortController === "function"
        ? new root.AbortController()
        : null;
      var timeout = controller
        ? root.setTimeout(function () { controller.abort(); }, 2500)
        : null;

      lookupPromise = root.fetch(endpoint || GEO_ENDPOINT, {
        credentials: "omit",
        referrerPolicy: "no-referrer",
        signal: controller ? controller.signal : undefined
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error("Geolocation request failed");
          }

          return response.json();
        })
        .then(originFromGeo)
        .catch(function () {
          return ORIGINS.UNITED_STATES;
        })
        .then(function (origin) {
          if (timeout) {
            root.clearTimeout(timeout);
          }

          return origin;
        });
    }

    return lookupPromise;
  }

  function personalizeCallouts(callouts) {
    var unresolvedCallouts = callouts.filter(function (callout) {
      return callout.dataset.inStock === "true" && !callout.dataset.origin;
    });

    if (!unresolvedCallouts.length || typeof root.fetch !== "function") {
      return;
    }

    var storefrontCountry = String(unresolvedCallouts[0].dataset.country || "").toUpperCase();
    if (storefrontCountry && storefrontCountry !== "US") {
      updateCallouts(unresolvedCallouts, ORIGINS.UNITED_STATES);
      return;
    }

    var cachedOrigin = readCachedOrigin();
    if (cachedOrigin) {
      updateCallouts(unresolvedCallouts, cachedOrigin);
      return;
    }

    unresolvedCallouts.forEach(function (callout) {
      callout.setAttribute("aria-busy", "true");
    });

    var geoEndpoint = unresolvedCallouts[0].dataset.geoEndpoint || GEO_ENDPOINT;
    lookupOrigin(geoEndpoint).then(function (origin) {
      cacheOrigin(origin);
      updateCallouts(unresolvedCallouts, origin);
    });
  }

  function init(document) {
    if (!document) {
      return;
    }

    var callouts = Array.prototype.slice.call(
      document.querySelectorAll(".js-shipping-origin:not([data-shipping-origin-ready])")
    );

    if (!callouts.length) {
      return;
    }

    callouts.forEach(function (callout) {
      callout.setAttribute("data-shipping-origin-ready", "");
      updateInventoryState([callout], callout.dataset.inStock === "true");
    });

    personalizeCallouts(callouts);
  }

  function handleVariantUpdate(event, document) {
    var detail = event && event.detail;
    if (!detail || !document) {
      return;
    }

    var scope = document;
    if (detail.sectionClass) {
      scope = document.querySelector(detail.sectionClass) || document;
    }

    var callouts = Array.prototype.slice.call(
      scope.querySelectorAll(".js-shipping-origin")
    );

    if (!callouts.length) {
      return;
    }

    var inStockCallouts = callouts.filter(function (callout) {
      var inStock = variantShowsPromise(detail.variant, callout);
      updateInventoryState([callout], inStock);
      return inStock;
    });

    if (inStockCallouts.length) {
      personalizeCallouts(inStockCallouts);
    }
  }

  return {
    ORIGINS: ORIGINS,
    handleVariantUpdate: handleVariantUpdate,
    init: init,
    nearestOrigin: nearestOrigin,
    originFromGeo: originFromGeo,
    variantIsInStock: variantIsInStock,
    variantIsPreorder: variantIsPreorder,
    variantShowsPromise: variantShowsPromise
  };
});
